import { parsePhoneNumberFromString } from 'libphonenumber-js/min';

/**
 * Phone numbers as a human reads them, not as we store them.
 *
 * Everything we persist is E.164 — `+48534001451` — because that is the only
 * shape that is unambiguous, comparable and dialable, and `toNsn`/`toE164` in
 * `@acs/shared` exist to keep it that way. But E.164 is a machine format: eleven
 * unbroken digits is exactly the string an owner cannot check against the phone
 * in their hand, which matters most on the one screen where they are asked to
 * confirm their own line.
 *
 * So display goes through here, and the grouping is the country's own — the
 * digits are never changed, only spaced:
 *
 *   +48600200300   →  +48 600 200 300     (PL mobile, 3-3-3)
 *   +48224567890   →  +48 22 456 78 90    (PL landline, area code + 2-2-2)
 *   +493012345678  →  +49 30 12345678     (DE, Berlin area code)
 *   +33612345678   →  +33 6 12 34 56 78   (FR, pairs)
 *
 * Which is why this leans on `libphonenumber-js` rather than a grouping table
 * of our own: national conventions are per-area-code, not per-country (compare
 * the two Polish numbers above), and a table we maintain would be wrong for
 * every city we had not thought about.
 *
 * The gaps are ordinary spaces, narrowed in CSS (`word-spacing` on `.mono`) —
 * a monospaced space is 0.6em, more than twice a proportional one, so at the
 * 26px the Settings hero prints a number at the groups drift apart into what
 * reads as several numbers. The obvious fix is a U+202F NARROW NO-BREAK SPACE in
 * the string, and it is wrong here: the IBM Plex Mono subset Google serves
 * declares U+2000–206F in its `unicode-range` but ships no glyph for U+2009 or
 * U+202F, so the character falls out of the font onto whatever the platform's
 * next monospace does with it — a normal-width space, or a tofu box. A plain
 * space also keeps the string plain, which matters because it is interpolated
 * into translated sentences.
 *
 * Kept in the portal, deliberately, and out of `@acs/shared`: this is a display
 * concern, and the gateway and functions have no use for it. Storage, routing
 * keys and comparisons all keep using E.164 — nothing formatted here is ever
 * written back or compared against anything. It is also NOT what a number input
 * holds: a field the owner types into shows the value they typed.
 */
export function formatPhoneDisplay(e164: string | null | undefined): string | null {
  if (!e164) return null;
  // Anything we cannot parse is shown exactly as stored. A number on screen is
  // something the owner acts on; a "prettier" guess at a shape we do not
  // recognise would be worse than the raw digits.
  const parsed = parsePhoneNumberFromString(e164.trim());
  return parsed?.isValid() ? parsed.formatInternational() : e164;
}

